import { useEffect } from "react";
import { useBlocker } from "react-router";
import { FormattedMessage } from "react-intl";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";

// Guards an unsaved transient match result (genesis kept=false draft) against loss on any exit
// (#227): in-app navigation shows the same discard dialog as the in-page "← Schließen" button; a
// hard exit (reload / tab close) triggers the native browser warning. Discard deletes the
// server-side draft via clearSolverResult, then proceeds; cancel stays put.
export function MatchDraftGuard({
  hasUnsavedTransient,
  onDiscard,
}: {
  hasUnsavedTransient: boolean;
  onDiscard: () => void;
}) {
  useEffect(() => {
    if (!hasUnsavedTransient) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedTransient]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedTransient && currentLocation.pathname !== nextLocation.pathname,
  );

  return (
    <ConfirmDialog
      open={blocker.state === "blocked"}
      title={
        <FormattedMessage
          id="match.result.discardUnsavedConfirm.title"
          defaultMessage="Ungespeichertes Ergebnis verwerfen?"
        />
      }
      message={
        <FormattedMessage
          id="match.result.discardUnsavedConfirm.message"
          defaultMessage="Das berechnete Ergebnis wurde noch nicht gespeichert und geht beim Zurückgehen verloren."
        />
      }
      confirmLabel={
        <FormattedMessage id="match.result.discardUnsavedConfirm.confirm" defaultMessage="Verwerfen" />
      }
      onConfirm={() => {
        onDiscard();
        blocker.proceed?.();
      }}
      onCancel={() => blocker.reset?.()}
    />
  );
}
